import { useState } from "react";
import { Pill, EmptyState } from "@/components/common";
import { TIERS } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const Cell = ({ children, className }) => <td className={cn("px-4 py-3 text-sm", className)}>{children}</td>;

export function RankingComparison({ data }) {
  const evs = data.evaluators.filter((e) => e.status === "SUBMITTED");
  if (!evs.length) return <EmptyState testId="ranking-empty" title="Henüz tamamlanmış değerlendirme yok" description="Sıralama karşılaştırması yalnızca gönderilmiş değerlendirmeleri içerir." />;
  const rows = [...data.candidates].sort((a, b) => {
    const pa = Object.values(data.rankings[a.id] || {}).reduce((s, v) => s + v, 0);
    const pb = Object.values(data.rankings[b.id] || {}).reduce((s, v) => s + v, 0);
    return pa - pb;
  });
  return (
    <div className="plena-card overflow-x-auto" data-testid="ranking-comparison">
      <table className="w-full min-w-[560px]">
        <thead>
          <tr className="border-b border-plena-border text-left">
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-plena-text2">Aday</th>
            {evs.map((e) => <th key={e.assignment_id} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-plena-text2">{e.name}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-plena-border">
          {rows.map((c) => (
            <tr key={c.id} data-testid={`ranking-row-${c.id}`}>
              <Cell><p className="font-medium">{c.display_name}</p>{c.title && <p className="text-xs text-plena-text2">{c.title}</p>}</Cell>
              {evs.map((e) => {
                const pos = data.rankings[c.id]?.[e.assignment_id];
                const ov = data.overall_scores?.[c.id]?.[e.assignment_id];
                return <Cell key={e.assignment_id} className="text-center"><span className={cn("inline-flex h-8 w-8 items-center justify-center rounded-full font-heading text-sm font-semibold", pos === 1 ? "bg-plena-text text-white" : "bg-plena-surface2")}>{pos ? `#${pos}` : "—"}</span>{ov != null && <span className="ml-2 text-xs text-plena-text2">{ov}/100</span>}</Cell>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-4 py-3 text-xs text-plena-text2">Ham sıralamalar ve genel uygunluk puanları (0–100) gösterilir; ortalama veya konsensüs hesaplanmaz.</p>
    </div>
  );
}

export function CategoryPriorityComparison({ data }) {
  const evs = data.evaluators.filter((e) => Object.keys(data.category_rankings?.[e.assignment_id] || {}).length);
  if (!evs.length) return <EmptyState testId="priority-empty" title="Henüz kategori önceliği girilmedi" />;
  const rows = [...data.criteria].sort((a, b) => {
    const s = (c) => evs.reduce((acc, e) => acc + (data.category_rankings[e.assignment_id]?.[c.id]?.position || 99), 0);
    return s(a) - s(b);
  });
  return (
    <div className="plena-card overflow-x-auto" data-testid="priority-comparison">
      <table className="w-full min-w-[560px]">
        <thead>
          <tr className="border-b border-plena-border text-left">
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-plena-text2">Kategori</th>
            {evs.map((e) => <th key={e.assignment_id} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-plena-text2">{e.name}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-plena-border">
          {rows.map((c) => (
            <tr key={c.id} data-testid={`priority-row-${c.id}`}>
              <Cell><p className="font-medium">{c.name}</p></Cell>
              {evs.map((e) => {
                const r = data.category_rankings[e.assignment_id]?.[c.id];
                return <Cell key={e.assignment_id} className="text-center">{r ? <><span className={cn("inline-flex h-8 w-8 items-center justify-center rounded-full font-heading text-sm font-semibold", r.position === 1 ? "bg-plena-text text-white" : "bg-plena-surface2")}>#{r.position}</span><span className="ml-2 text-xs text-plena-text2">{TIERS[r.tier] || "—"}</span></> : "—"}</Cell>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-4 py-3 text-xs text-plena-text2">Her değerlendiricinin bu pozisyon için kategori sıralaması ve önem seviyesi; ağırlık türetme sonradan yapılır.</p>
    </div>
  );
}

export function CriterionComparison({ data }) {
  const [cid, setCid] = useState(data.candidates[0]?.id);
  const evs = data.evaluators;
  const cand = data.candidates.find((c) => c.id === cid);
  if (!cand) return <EmptyState testId="criterion-empty" title="Aday bulunamadı" />;
  const cats = [...data.categories].sort((a, b) => a.display_order - b.display_order);
  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      <aside className="plena-card self-start divide-y divide-plena-border" data-testid="criterion-candidate-list">
        {data.candidates.map((c) => (
          <button key={c.id} data-testid={`criterion-candidate-${c.id}`} onClick={() => setCid(c.id)} className={cn("block w-full px-4 py-3 text-left text-sm transition-colors duration-150", c.id === cid ? "bg-plena-surface2 font-semibold" : "hover:bg-plena-surface2/60")}>{c.display_name}</button>
        ))}
      </aside>
      <div className="space-y-6">
        <div><p className="eyebrow mb-1">Aday</p><h3 className="font-heading text-xl font-semibold">{cand.display_name}</h3>{cand.title && <p className="text-sm text-plena-text2">{cand.title}</p>}</div>
        <section className="plena-card overflow-hidden">
          <header className="border-b border-plena-border bg-plena-surface2/60 px-5 py-3"><p className="font-heading text-sm font-semibold">Kategori puanları</p></header>
          <div className="divide-y divide-plena-border">
            {[...data.criteria].sort((a, b) => (cats.findIndex((c) => c.id === a.category_id)) - (cats.findIndex((c) => c.id === b.category_id))).map((cr) => (
              <div key={cr.id} data-testid={`criterion-compare-${cr.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start">
                <p className="flex-1 font-medium">{cr.name}</p>
                <div className="flex flex-wrap gap-2">
                  {evs.map((e) => {
                    const s = data.scores[cid]?.[cr.id]?.[e.assignment_id];
                    const label = !s ? "—" : s.evidence_status === "INSUFFICIENT" ? "Bilgi yok" : cr.evaluation_type === "BINARY" ? (s.score === 1 ? "Evet" : "Hayır") : s.score;
                    return (
                      <div key={e.assignment_id} className="flex min-w-[96px] flex-col items-center rounded-xl bg-plena-surface2 px-3 py-2" title={s?.comment || ""}>
                        <span className="text-[11px] text-plena-text2">{e.name.split(" ")[0]}</span>
                        <span className={cn("font-heading text-lg font-bold", label === "—" && "text-plena-muted", label === "Bilgi yok" && "text-sm font-medium text-plena-text2")}>{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
        {evs.some((e) => data.comments[cid]?.[e.assignment_id]) && (
          <section className="plena-card px-5 py-4">
            <p className="eyebrow mb-3">Genel değerlendirme notları</p>
            {evs.map((e) => data.comments[cid]?.[e.assignment_id] && <p key={e.assignment_id} className="mb-2 text-sm"><span className="font-medium">{e.name}:</span> {data.comments[cid][e.assignment_id]}</p>)}
          </section>
        )}
        <div className="flex flex-wrap gap-2 text-xs text-plena-text2">{evs.map((e) => <Pill key={e.assignment_id} tone={e.status === "SUBMITTED" ? "green" : "yellow"}>{e.name} · {e.status === "SUBMITTED" ? "Tamamlandı" : "Devam ediyor"}</Pill>)}</div>
      </div>
    </div>
  );
}
